// A2F-695: dedicated /auth/login route for the Pro upgrade card's
// "Sign in" CTA. The card lives in the Word taskpane (mychronaView.ts)
// and opens a new tab pointed here when gate-pro reports `signin_needed`.
// Post-login, navigates to ?redirect= (same-origin only) so the user
// lands back where they started; default destination is /configure.
//
// The /configure route already shows an inline login form for anonymous
// visitors (App.tsx ConfigureLoginForm), but it always lands on
// /configure post-login — wrong destination when the entry point was a
// per-rule sign-in nudge from the taskpane.

import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabaseClient'
import { TermsPrivacyFooter } from '../components/TermsPrivacyFooter'

const DEFAULT_REDIRECT = '/configure'

// Same-origin guard: a `?redirect=` param coming from a Word taskpane URL
// could in theory carry an attacker-supplied destination. Restrict to
// relative paths starting with "/" and reject "//" (which browsers may
// resolve as protocol-relative). Anything else falls back to /configure.
function safeRedirect(raw: string | null): string {
  if (!raw) return DEFAULT_REDIRECT
  if (!raw.startsWith('/') || raw.startsWith('//')) return DEFAULT_REDIRECT
  return raw
}

export function AuthLoginPage() {
  const { session, loading, signInWithPassword } = useAuth()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const redirect = safeRedirect(params.get('redirect'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const [resetting, setResetting] = useState(false)

  useEffect(() => {
    document.title = 'Sign in — Chrona Pro'
  }, [])

  if (loading) return null
  if (session) return <Navigate to={redirect} replace />

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    const { error: signInError } = await signInWithPassword(email.trim(), password)
    setSubmitting(false)
    if (signInError) {
      setError(signInError)
      return
    }
    // Success: AuthProvider's onAuthStateChange will set `session`,
    // which triggers the <Navigate> above on the next render.
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError('Enter your email address above, then click Forgot password.')
      return
    }
    setResetting(true)
    setError(null)
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        { redirectTo: `${window.location.origin}/update-password` },
      )
      if (resetError) throw resetError
      setResetSent(true)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not send reset email.')
    } finally {
      setResetting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-charcoal font-sans grid place-items-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-2">
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-brand-orange text-white text-sm font-bold">
            C
          </div>
          <span className="text-sm font-semibold tracking-wider text-brand-charcoal">
            CHRONA{' '}
            <span className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded text-brand-orange bg-brand-orange/10">
              PRO
            </span>
          </span>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 shadow-sm">
          <h1 className="text-base font-semibold text-center">Sign in to your account</h1>

          {resetSent ? (
            <div className="text-sm text-center text-slate-600 space-y-3">
              <p>
                Check your inbox — we sent a password reset link to{' '}
                <strong>{email}</strong>.
              </p>
              <button
                type="button"
                onClick={() => setResetSent(false)}
                className="text-brand-orange hover:underline text-xs"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label
                  htmlFor="login-email"
                  className="block text-xs font-medium text-slate-600"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="login-password"
                  className="block text-xs font-medium text-slate-600"
                >
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                />
              </div>

              {error && (
                <p className="text-xs text-red-600 rounded-md bg-red-50 border border-red-200 px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-brand-orange text-white py-2.5 text-sm font-medium hover:bg-brand-orange/90 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                Sign in
              </button>

              <p className="text-xs text-center">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetting}
                  className="text-slate-500 hover:text-brand-orange disabled:opacity-50"
                >
                  {resetting ? 'Sending…' : 'Forgot password?'}
                </button>
              </p>
            </form>
          )}

          <p className="text-xs text-center text-slate-500">
            Don&apos;t have an account?{' '}
            <Link to="/" className="text-brand-orange font-medium hover:underline">
              Get started →
            </Link>
          </p>
        </div>
      </div>
      <div className="mt-8">
        <TermsPrivacyFooter />
      </div>
    </div>
  )
}
